import type { Vec3 } from '../geo/types';
import { createRng } from '../core/rng';
import type { CityTimeSnapshot } from '../time/cityTime';
import { AudioVoices } from './audioVoices';
import { MotionAudio } from './motionAudio';
import { RowingCadence, soundStroke } from './rowingAudio';
import { createWind } from './wind';
import { nearestSound, spatialSound, type SoundEmitter, type SoundKind } from './spatialAudio';
import { soundEffect, bellStrike } from './soundEffects';
import { BellClock } from './bellClock';

export type AudioActivity = 'walk' | 'fly' | 'boat';
const CADENCE: readonly [SoundKind, number, number][] = [
  ['water', 0.7, 1.5],
  ['rigging', 3, 8],
  ['cargo', 4, 10],
  ['carpentry', 3, 7],
  ['market', 2, 5],
  ['bird', 5, 14],
];

export function createSoundscape(
  ctx: AudioContext,
  sources: readonly SoundEmitter[],
  actors: () => readonly SoundEmitter[]
) {
  return new Soundscape(ctx, sources, actors);
}

class Soundscape {
  private readonly rng = createRng(1538);
  private readonly master: GainNode;
  private readonly breeze;
  private readonly voices: AudioVoices;
  private readonly next = new Map<SoundKind, number>();
  private readonly contacts = new Map<string, MotionAudio>();
  private readonly ownMotion = new MotionAudio();
  private readonly rowing = new RowingCadence();
  private activity: AudioActivity | null = null;
  private nextUpdate = 0;
  private readonly clock = new BellClock();
  private peal: { source: SoundEmitter; left: number; next: number } | null = null;
  private disposed = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly sources: readonly SoundEmitter[],
    private readonly actors: () => readonly SoundEmitter[]
  ) {
    this.master = ctx.createGain();
    this.master.gain.value = 0.4;
    this.master.connect(ctx.destination);
    this.breeze = createWind(ctx, this.master, this.rng);
    this.voices = new AudioVoices(ctx, this.master);
    this.reset();
  }

  private reset(): void {
    const now = this.ctx.currentTime;
    for (const [kind, from, to] of CADENCE) this.next.set(kind, now + this.rng.range(from, to));
    this.clock.reset();
    this.peal = null;
    this.contacts.clear();
    this.ownMotion.reset();
    this.rowing.reset();
  }

  private ambience(
    position: Vec3,
    yaw: number,
    now: number,
    activity: number,
    actors: readonly SoundEmitter[]
  ): void {
    for (const [kind, from, to] of CADENCE) {
      if (now < (this.next.get(kind) ?? 0)) continue;
      this.next.set(kind, now + this.rng.range(from, to));
      if (activity < 0.05 && !['water', 'rigging'].includes(kind)) continue;
      const sound = nearestSound(kind === 'bird' ? actors : this.sources, kind, position, yaw);
      if (sound) soundEffect(this.voices, sound, this.rng, activity);
    }
  }

  private bells(position: Vec3, yaw: number, now: number, time: CityTimeSnapshot): void {
    const event = this.clock.update(time);
    if (event.reset) {
      this.peal = null;
      this.voices.stop();
    }
    if (event.strikes > 0) {
      const sound = nearestSound(this.sources, 'bell', position, yaw);
      if (sound) this.peal = { source: sound.emitter, left: event.strikes, next: now };
    }
    if (this.peal && now >= this.peal.next) {
      bellStrike(this.voices, spatialSound(this.peal.source, position, yaw));
      this.peal.next = now + 2.8;
      if (--this.peal.left === 0) this.peal = null;
    }
  }

  private movement(
    position: Vec3,
    yaw: number,
    now: number,
    mode: AudioActivity,
    actors: readonly SoundEmitter[]
  ): void {
    if (mode !== this.activity) {
      this.ownMotion.reset();
      this.rowing.reset();
      this.activity = mode;
    }
    const contact = mode === 'walk' ? this.ownMotion.update(position, now) : null;
    const stroke = mode === 'boat' ? this.rowing.update(position, now) : null;
    if (stroke) soundStroke(this.voices, stroke, position, yaw, this.rng);
    if (contact) {
      const own: SoundEmitter = {
        id: 'listener',
        kind: 'footstep',
        at: position,
        reach: 4,
        level: 0.13,
      };
      soundEffect(this.voices, spatialSound(own, position, yaw), this.rng, 1);
    }
    for (const actor of actors) {
      if (actor.kind !== 'footstep' && actor.kind !== 'hooves') continue;
      let motion = this.contacts.get(actor.id);
      if (!motion) {
        motion = new MotionAudio(
          actor.kind === 'hooves' ? 0.3 : 0.65,
          actor.kind === 'hooves' ? 0.16 : 0.28
        );
        this.contacts.set(actor.id, motion);
      }
      const step = motion.update(actor.at, now),
        sound = spatialSound(actor, position, yaw);
      if (step && sound.volume > 0.002) soundEffect(this.voices, sound, this.rng, 1);
    }
  }

  update(
    position: Vec3,
    yaw: number,
    mode: AudioActivity,
    time: CityTimeSnapshot,
    activity: number
  ): void {
    if (this.disposed || this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;
    if (now < this.nextUpdate) return;
    this.nextUpdate = now + 0.08;
    this.voices.update(position, yaw);
    this.breeze.gain.gain.setTargetAtTime(
      0.07 + 0.025 * (1 + Math.sin(now * 0.13) * Math.sin(now * 0.29)),
      now,
      1.2
    );
    const actors = this.actors();
    this.bells(position, yaw, now, time);
    this.ambience(position, yaw, now, activity, actors);
    this.movement(position, yaw, now, mode, actors);
  }

  setMuted(muted: boolean): void {
    this.master.gain.setTargetAtTime(muted ? 0 : 0.4, this.ctx.currentTime, 0.08);
    this.voices.stop();
    this.reset();
  }

  dispose(): void {
    this.disposed = true;
    this.breeze.dispose();
    this.voices.stop();
    this.master.disconnect();
  }
}
